import { ImportCandidate, MonitoringConfig } from "../types/domain";
import { pendingCases } from "../data/pending-cases";
import { syncScenarios } from "../data/sync-scenarios";

type OnNewCase = (candidate: ImportCandidate) => void;
type OnExistingCaseUpdate = (targetMemorialId: string, candidate: ImportCandidate, changeDescription: string) => void;

let timer: ReturnType<typeof setTimeout> | null = null;
let discoveredIndex = 0;
let syncScenarioIndex = 0;

export function startMonitoring(
  config: MonitoringConfig,
  onNewCase: OnNewCase,
  onExistingUpdate?: OnExistingCaseUpdate,
): void {
  stopMonitoring();
  if (!config.enabled) return;

  const scanInterval = Math.max(1, config.intervalMinutes ?? 30) * 60 * 1000;

  function scheduleNext() {
    const hasNew = discoveredIndex < pendingCases.length;
    const hasUpdates = syncScenarioIndex < syncScenarios.length;
    if (!hasNew && !hasUpdates) return;

    timer = setTimeout(() => {
      // Alternate between new cases and updates for demo variety
      if (hasNew) {
        const candidate = pendingCases[discoveredIndex];
        if (candidate) {
          discoveredIndex++;
          onNewCase(candidate);
        }
      } else if (hasUpdates && onExistingUpdate) {
        const scenario = syncScenarios[syncScenarioIndex];
        if (scenario) {
          syncScenarioIndex++;
          onExistingUpdate(scenario.targetMemorialId, scenario.candidate, scenario.changeDescription);
        }
      }
      scheduleNext();
    }, scanInterval);
  }

  scheduleNext();
}

export function stopMonitoring(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

export function checkNow(
  onNewCase: OnNewCase,
  onExistingUpdate?: OnExistingCaseUpdate,
): boolean {
  // Prefer new cases; fall back to update scenarios
  if (discoveredIndex < pendingCases.length) {
    const candidate = pendingCases[discoveredIndex];
    if (candidate) {
      discoveredIndex++;
      onNewCase(candidate);
      return true;
    }
  } else if (syncScenarioIndex < syncScenarios.length && onExistingUpdate) {
    const scenario = syncScenarios[syncScenarioIndex];
    if (scenario) {
      syncScenarioIndex++;
      onExistingUpdate(scenario.targetMemorialId, scenario.candidate, scenario.changeDescription);
      return true;
    }
  }
  return false;
}

export function resetMonitoring(): void {
  stopMonitoring();
  discoveredIndex = 0;
  syncScenarioIndex = 0;
}

export function getPendingCount(): number {
  return (pendingCases.length - discoveredIndex) + (syncScenarios.length - syncScenarioIndex);
}

export function getNewCasePendingCount(): number {
  return pendingCases.length - discoveredIndex;
}

export function getSyncScenarioPendingCount(): number {
  return syncScenarios.length - syncScenarioIndex;
}
